import classNames from 'classnames'
import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { DropdownDynamic } from 'legacy/lut/library/dropdown'
import { ValidateInput } from 'legacy/lut/library/validation'
import { appGetState } from 'legacy/lut/store'
import React, { useEffect, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Controller, ValidationStatus } from 'core-components/library/validation'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import MeasureController from 'core/analytics/MeasureController'
import { CrudStoreItemCore } from 'core/classes/services/InitializationServiceCore'
import { CrudStore } from 'core/types/crudStore'

import { CommitmentContactSelection } from './CommitmentContactSelection'

import styles from './CommitmentEditor.module.scss'

interface IProps {
  pid: string
  metrics: MeasureController
  flexWidth?: FlexDirectionBreak

  item: IObjectAny
  refuse: boolean
  canSign?: boolean
  isPartnerUser?: boolean
  canSignOnBehalf: boolean

  onCancel: () => void
  onApply: (item: IObjectAny, refuseComment?: string) => void
}

export const CommitmentEditor = (props: IProps) => {
  const [item, setItem] = useState<IObjectAny>(props.item)
  const [changed, setChanged] = useState(props.item.type !== 'partner')
  const [showContactDialog, setShowContactDialog] = useState(false)
  const [currStatus, setCurrStatus] = useState(
    props.item.type !== 'partner' || props.isPartnerUser
      ? ValidationStatus.Valid
      : ValidationStatus.Invalid
  )
  const [email, setEmail] = useState(appGetState().user.email)
  const [refuseComment, setRefuseComment] = useState('')

  const usersCrud = CrudStore.get(CrudStoreItemCore.Users, 'pbm2partner')

  const onChange = (newValue: string, fieldName: string) => {
    item[fieldName] = newValue
    setItem({ ...item })
    !changed && setChanged(true)
    if (item.contactId != undefined) {
      setCurrStatus(ValidationStatus.Valid)
    }
  }

  useEffect(() => {
    if (item.type !== 'partner' || (item.type === 'partner' && props.isPartnerUser)) {
      const user = appGetState().user
      setItem({
        ...item,
        email: user.email,
        contactId: user.id,
        name: `${user.firstName} ${user.lastName}`,
      })
      setChanged(true)
    }
  }, [])

  let userTypeTag = ''
  if (item.type === 'partner') {
    if (item.item && item.item.SignerEmail === email) {
      userTypeTag = '-user'
    } else {
      userTypeTag = '-partner'
    }
  }
  const translationTag = props.refuse
    ? 'partnercommitment-refuse-confirmation'
    : `partnercommitment-signoff${userTypeTag}-confirmation`

  if (
    !showContactDialog &&
    currStatus !== ValidationStatus.Valid &&
    item.type === 'partner' &&
    !props.isPartnerUser
  ) {
    setShowContactDialog(true)
  }

  return (
    <div className={styles.root}>
      <Controller
        onValidationStatusChange={({ status }) => {
          status !== 'WIP' && setCurrStatus(status)
        }}
      >
        <RenderWhen condition={currStatus === ValidationStatus.Valid || props.refuse}>
          <label className={styles.label}>
            <Translation span text={translationTag} htmlAvailable />
            <strong>{item.name}</strong>
            <Translation span text="partnercommitment-signoff-email" />
            <strong>{item.email}</strong>?
          </label>
        </RenderWhen>

        {item.type === 'hp' && (
          <DropdownDynamic
            classes={{
              controlClasses: {
                root: classNames(styles.autocomplete, styles.hidden),
              },
            }}
            fieldsToSearch={['Id', 'firstName', 'lastName']}
            calculateCaption={(item) => `${item.id} (${item.roleIds.join(', ')})`}
            source={usersCrud}
            value={email}
            onChange={(value) => {
              setEmail(value)
              props.item.email = value
              props.item.contactId = value

              usersCrud.getById({ id: value, keyField: 'Id' }).then((result) => {
                setItem({
                  ...item,
                  name: result
                    ? `${result.FirstName || result.firstName} ${
                        result.LastName || result.lastName
                      }`
                    : item.name,
                  email: value,
                  contactId: value,
                })
                !changed && setChanged(true)
                if (item.contactId != undefined && item.date != undefined) {
                  setCurrStatus(ValidationStatus.Valid)
                }
              })
            }}
          />
        )}

        {props.refuse && (
          <ValidateInput
            margin={'md'}
            label="Date"
            value={refuseComment}
            onChange={(value) => setRefuseComment(value)}
            translation="partnercommitment-refuse-comment"
            checkFn={(value) => ValidationHelper.checkString(value)}
          />
        )}
      </Controller>

      <div className={styles.footer}>
        {(props.refuse ||
          (props.canSign &&
            (!(item.type === 'partner' && !props.isPartnerUser) || props.canSignOnBehalf))) && (
          <Button
            disabled={!changed || currStatus !== ValidationStatus.Valid}
            className={styles.footer__button}
            onClick={async () => {
              setChanged(false)
              if (props.refuse) {
                props.onApply(item, refuseComment)
              } else {
                props.onApply(item)
              }
            }}
          >
            <Translation
              text={
                props.refuse
                  ? 'partnercommitment-refuse'
                  : item.type === 'partner' && !props.isPartnerUser
                    ? 'partnercommitment-sign-off-on-behave'
                    : 'partnercommitment-sign-off'
              }
              span
            />
          </Button>
        )}
        <Button
          className={styles.footer__button}
          onClick={async () => {
            setChanged(false)
            props.onCancel()
          }}
        >
          <Translation text="partnercommitment-cancel" span />
        </Button>
      </div>

      <ModalDialog
        show={showContactDialog}
        size={'auto'}
        content={CommitmentContactSelection}
        contentProps={{
          pid: props.pid,
          metrics: props.metrics,
          flexWidth: props.flexWidth,

          onApply: (contact: IObjectAny) => {
            setShowContactDialog(false)
            setItem({ ...item })
            item.email = contact.Email
            item.contactId = contact.EntryID
            item.language = contact.PreferredLanguage
            onChange(contact.FirstName + ' ' + contact.LastName, 'name')
          },
        }}
        onClose={() => {
          setShowContactDialog(false)
          props.onCancel()
        }}
      />
    </div>
  )
}
